/**
 * Программная часть слоя движения (FE-13).
 *
 * Основная масса анимаций живёт в CSS — `src/app/styles/motion.css`. Сюда
 * вынесено то, чего декларативно не выразить: анимации по измеренной
 * геометрии (FLIP), одометр цифр, жест на шите. Все они обязаны уважать
 * системную настройку «уменьшить движение» и молча вырождаться в конечное
 * состояние там, где Web Animations API недоступен — в jsdom, на котором
 * гоняются тесты, `element.animate` не реализован вовсе.
 *
 * Это единственный модуль вне `motion.css`, которому разрешены литеральные
 * длительности и кривые: значения-фолбэки нужны ровно на случай, когда
 * переменную не удалось прочитать (тест-окружение, ранний кадр до применения
 * стилей). Правило проверяется тестом src/test/quality/rules.test.ts.
 */

import type { CSSProperties } from "react";

export type MotionDuration =
  | "instant"
  | "fast"
  | "base"
  | "slow"
  | "page"
  | "stagger"
  | "toast";
export type MotionEase = "standard" | "out-quint" | "in-quint" | "spring";

/** Дубли значений из motion.css — используются, только если CSS не прочитался. */
const DURATION_FALLBACK: Record<MotionDuration, number> = {
  instant: 90,
  fast: 140,
  base: 220,
  slow: 320,
  page: 380,
  stagger: 40,
  toast: 4000,
};

const EASE_FALLBACK: Record<MotionEase, string> = {
  standard: "cubic-bezier(0.2, 0, 0, 1)",
  "out-quint": "cubic-bezier(0.22, 1, 0.36, 1)",
  "in-quint": "cubic-bezier(0.64, 0, 0.78, 0)",
  spring: "cubic-bezier(0.34, 1.56, 0.64, 1)",
};

/** Токены статичны, поэтому читаются из CSS один раз за жизнь страницы. */
const cache = new Map<string, string>();

function readVar(name: string): string {
  const cached = cache.get(name);
  if (cached !== undefined) return cached;

  const value =
    typeof window === "undefined"
      ? ""
      : getComputedStyle(document.documentElement).getPropertyValue(name).trim();

  cache.set(name, value);
  return value;
}

/** Длительность токена в миллисекундах — для `element.animate` и таймеров. */
export function duration(token: MotionDuration): number {
  const raw = readVar(`--motion-${token}`);
  const parsed = Number(raw.replace("ms", ""));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DURATION_FALLBACK[token];
}

/** Кривая токена строкой — в том виде, в каком её принимает `easing`. */
export function easing(name: MotionEase): string {
  return readVar(`--ease-${name}`) || EASE_FALLBACK[name];
}

/**
 * Системная настройка «уменьшить движение». Читается на каждый вызов, а не
 * запоминается: пользователь может переключить её, не перезагружая мини-апп.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Умеет ли окружение анимировать вообще.
 *
 * Web Animations API здесь работает признаком «настоящий браузер»: в jsdom,
 * на котором идут тесты, его нет, и любая пауза «на время анимации»
 * превратилась бы в подвисание интерфейса, которого никто не увидит. Тем же
 * признаком выключается всё при системной настройке «уменьшить движение».
 */
export function canAnimate(): boolean {
  if (typeof Element === "undefined") return false;
  if (typeof Element.prototype.animate !== "function") return false;
  return !prefersReducedMotion();
}

/**
 * Обёртка над `element.animate`.
 *
 * @returns `undefined`, если анимация не запускалась — при уменьшенном
 * движении или в окружении без Web Animations API. Вызывающий обязан быть
 * к этому готов: конечное состояние должно достигаться и без анимации.
 */
export function animate(
  element: Element,
  keyframes: Keyframe[],
  options: KeyframeAnimationOptions,
): Animation | undefined {
  if (!canAnimate()) return undefined;
  if (typeof element.animate !== "function") return undefined;

  try {
    return element.animate(keyframes, { fill: "none", ...options });
  } catch (error) {
    console.warn("[motion] animate не поддержан", error);
    return undefined;
  }
}

/**
 * FLIP: элемент уже переехал в новую позицию, и мы «возвращаем» его в старую
 * трансформацией, а потом отпускаем. Так переезд активного индикатора
 * навигации и перестановка тостов выглядят движением, а не скачком.
 *
 * @param previous геометрия ДО перерисовки; `undefined` — элемент только
 * появился, возвращать его неоткуда.
 */
export function flipFrom(element: HTMLElement, previous: DOMRect | undefined): void {
  if (!previous) return;

  const next = element.getBoundingClientRect();
  const dx = previous.left - next.left;
  const dy = previous.top - next.top;

  // Меньше пикселя — это не переезд, а погрешность округления.
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;

  animate(
    element,
    [
      { transform: `translate3d(${String(dx)}px, ${String(dy)}px, 0)` },
      { transform: "translate3d(0, 0, 0)" },
    ],
    { duration: duration("base"), easing: easing("out-quint") },
  );
}

/**
 * Задержка каскада для элемента списка. Отдаётся inline-стилем, а класс
 * `stagger` из motion.css превращает её в реальную паузу перед входом.
 */
/**
 * Вертикальный FLIP по `offsetTop`.
 *
 * Обычный `getBoundingClientRect` здесь не годится: он учитывает текущий
 * `transform`, и элемент, который прямо сейчас анимируется, вернул бы
 * промежуточную позицию. `offsetTop` — это раскладка, анимации на неё не
 * влияют.
 *
 * @returns новую позицию — её и надо запомнить до следующего перерасчёта.
 */
export function flipFromTop(element: HTMLElement, previousTop: number | undefined): number {
  const top = element.offsetTop;
  if (previousTop === undefined || Math.abs(previousTop - top) < 1) return top;

  animate(
    element,
    [
      { transform: `translate3d(0, ${String(previousTop - top)}px, 0)` },
      { transform: "translate3d(0, 0, 0)" },
    ],
    { duration: duration("base"), easing: easing("out-quint") },
  );

  return top;
}

export function staggerIndex(index: number): CSSProperties {
  return { "--i": index } as CSSProperties;
}
